import axios from 'axios'
import { Folders } from '@services/s3/constants'
import { useShowToast } from '@hooks/useShowToast'

interface UseFileUploadResult {
    handleUpload: (files: { src: string; title: string; data: string }[]) => Promise<{
        status: 'success' | 'reject'
    }>
}

function base64ToFile(base64String, fileName, mimeType) {
    const base64Data = base64String.split(',')[1]

    const byteCharacters = atob(base64Data)
    const byteNumbers = new Array(byteCharacters.length)
    for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i)
    }

    const byteArray = new Uint8Array(byteNumbers)

    const blob = new Blob([byteArray], { type: mimeType })

    const file = new File([blob], fileName, { type: mimeType })

    return file
}

export const useFileS3ArrayUpload = (): UseFileUploadResult => {
    const { showToast } = useShowToast()

    const handleUpload = async (files: { src: string; title: string; data: string }[]): Promise<{ status: 'success' | 'reject' }> => {
        const filesToSave = files.map(({ src, title, data }) => {
            const type = title.split('.').pop() as string

            const contentType = type ? `image/${type}` : 'application/octet-stream'

            return {
                name: src,
                src: data,
                title,
                type,
                contentType,
            }
        })

        const response = await axios.post<{ urls: { url: string; name: string } }, any, { name: string; key: Folders }[]>(
            '/api/cdn/s3/upload-pack',
            filesToSave.map(({ name }) => ({ name, key: 'posts' })),
        )

        const { urls } = await response.data

        const dto = filesToSave.map(({ src, name: fileName, contentType }) => ({
            url: urls.find(({ name }) => name === fileName)?.url,
            contentType,
            file: base64ToFile(src, fileName, contentType),
            name: fileName,
        }))

        try {
            const uploadResponse = await Promise.all(
                dto.map(async ({ url, file, contentType }) => {
                    if (url) {
                        return await fetch(url, {
                            method: 'PUT',
                            body: file,
                            headers: {
                                'Content-Type': contentType,
                            },
                        })
                    }
                }),
            )

            if (!uploadResponse.every((r) => r?.ok)) {
                showToast({
                    variant: 'error',
                    text: 'error.default',
                })
                return {
                    status: 'reject',
                }
            }
        } catch (e) {
            showToast({
                variant: 'error',
                text: 'error.default',
            })
        }

        return {
            status: 'success',
        }
    }

    return {
        handleUpload,
    }
}
